/**
 * Connexion en ecran partage : a gauche (grand ecran) ce que l'application
 * apporte, a droite le formulaire. Sur mobile, seul le formulaire reste, sous
 * une marque compacte.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { destinationMemorisee, useAuth } from '../lib/auth';
import { messageErreur } from '../lib/api';
import { CadreAuthentification } from '../composants/CadreAuthentification';
import { Bandeau, Bouton, Champ } from '../composants/ui';

export function PageConnexion() {
  const { connexion, estConnecte, chargement } = useAuth();
  const destination = destinationMemorisee(useLocation());

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  if (estConnecte) return <Navigate to={destination} replace />;

  const soumettre = async (evenement: FormEvent<HTMLFormElement>): Promise<void> => {
    evenement.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await connexion(email.trim(), motDePasse);
    } catch (cause) {
      setErreur(messageErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <CadreAuthentification
      titre="Connexion"
      sousTitre="Accédez à votre espace de prospection."
      pied={
        <p className="mt-8 text-xs leading-relaxed text-encre-discrete">
          Les données personnelles traitées se limitent aux représentants légaux publiés par les
          registres officiels ; toute demande d'opposition est enregistrée sur la fiche concernée.
        </p>
      }
    >
      <form onSubmit={soumettre} className="space-y-4" noValidate>
        <Champ libelle="Adresse électronique">
          {(id) => (
            <input
              id={id}
              name="email"
              type="email"
              autoComplete="username"
              required
              className="champ"
              value={email}
              onChange={(evenement) => setEmail(evenement.target.value)}
              placeholder="prenom.nom@exemple.fr"
            />
          )}
        </Champ>

        <Champ libelle="Mot de passe">
          {(id) => (
            <input
              id={id}
              name="motDePasse"
              type="password"
              autoComplete="current-password"
              required
              className="champ"
              value={motDePasse}
              onChange={(evenement) => setMotDePasse(evenement.target.value)}
            />
          )}
        </Champ>

        {erreur && (
          <Bandeau ton="danger" role="alert" titre="Connexion refusée">
            {erreur}
          </Bandeau>
        )}

        <Bouton
          type="submit"
          variante="primaire"
          taille="lg"
          className="w-full"
          chargement={envoi}
          disabled={chargement || email === '' || motDePasse === ''}
        >
          {envoi ? 'Connexion en cours…' : 'Se connecter'}
        </Bouton>
      </form>
    </CadreAuthentification>
  );
}
